import React from 'react';
import { Tooltip, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { formatRequirementId, getRequirementLinkStyles } from '../../utils/requirementParser';
import { resolveIdDisplay, itemHashHref } from '../../utils/itemId';
import { isPlainLeftClick } from '../../utils/navClick';
import { usePref } from '../../services/prefs';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_ARRAY = Object.freeze([]);

/**
 * RequirementLink component for displaying clickable requirement ID references
 */
const RequirementLink = ({
  requirementId,
  onClick,
  variant = 'inline',
  showTooltip = true,
  requirements = EMPTY_ARRAY,
  ...props
}) => {
  const theme = useTheme();
  const showHumanIds = usePref('showHumanIds');

  const handleClick = (event) => {
    // Issue #339: a middle-click / ctrl-click / cmd-click / shift-click (or a
    // real right-click) must fall through to the browser's native "open in a
    // new tab/window" (or context menu) — only a plain left click is
    // intercepted to do the in-app hash-navigation manually.
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();

    // Set the hash to trigger navigation
    window.location.hash = `item=${requirementId}`;

    // Call onClick if provided
    if (onClick) {
      onClick(requirementId);
    }
  };

  // `requirementId` is always a friendly-form id (REQ-NNNN) parsed out of free
  // text (see requirementParser.renderTextWithRequirementLinks) — resolve it
  // against the loaded `requirements` so the `showHumanIds` pref decides what's
  // shown inline, with the other form revealed in the tooltip (#174). Falls
  // back to the plain uppercased id when the requirement isn't loaded/found.
  const matched = requirements.find(
    (req) => req.id === requirementId || req.requirement_id === requirementId || req.display_id === requirementId
  );
  const exists = Boolean(matched);
  const ids = matched ? resolveIdDisplay(matched, showHumanIds) : null;
  const formattedId = ids ? ids.primary : formatRequirementId(requirementId);
  const tooltipRaw = ids && ids.secondary ? ` (${ids.secondary})` : '';

  const linkContent = (
    <a
      href={itemHashHref(requirementId)}
      onClick={handleClick}
      style={{
        ...getRequirementLinkStyles(theme),
        display: 'inline-block',
        textDecoration: 'none',
        ...props.style
      }}
      {...props}
    >
      {formattedId}
    </a>
  );

  if (variant === 'chip') {
    return (
      <Chip
        component="a"
        href={itemHashHref(requirementId)}
        label={formattedId}
        size="small"
        color={exists ? 'primary' : 'default'}
        variant={exists ? 'filled' : 'outlined'}
        onClick={handleClick}
        clickable
        sx={{
          cursor: 'pointer',
          textDecoration: 'none',
          '&:hover': {
            backgroundColor: exists
              ? (theme.vars || theme).palette.primary.dark
              : (theme.vars || theme).palette.action.hover,
          }
        }}
      />
    );
  }

  if (showTooltip) {
    return (
      <Tooltip
        title={exists ? `View requirement ${formattedId}${tooltipRaw}` : `Requirement ${formattedId} not found`}
        arrow
      >
        {linkContent}
      </Tooltip>
    );
  }

  return linkContent;
};

RequirementLink.propTypes = {
  requirementId: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(['inline', 'chip']),
  showTooltip: PropTypes.bool,
  requirements: PropTypes.array,
  style: PropTypes.object
};

export default RequirementLink;